import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, Clock, BookOpen, TrendingUp, Calendar } from 'lucide-react';
import { mockArticles } from '../../data/mock';

const ArticlesSection = () => {
  const featuredArticles = mockArticles.filter(article => article.featured).slice(0, 3);

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background décoratif */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-purple-50/30"></div>
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-l from-blue-100 to-transparent rounded-full filter blur-3xl opacity-30"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-16">
          <div className="max-w-2xl mb-8 lg:mb-0">
            
            <h2 className="text-4xl md:text-5xl font-display font-black text-gray-900 mb-6">
              Guides complets
              <br />
              <span className="gradient-text">par des experts</span>
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Contenus premium validés par nos juristes et experts-comptables. 
              Tout pour réussir votre création d'entreprise.
            </p>
          </div>
          
          <Link to="/articles">
            <Button 
              variant="outline" 
              size="lg"
              className="btn-modern border-2 border-gray-300 hover:border-orange-500 hover:bg-orange-50 px-8 py-3 rounded-xl group"
            >
              Tous les articles
              <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* Grille des articles */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {featuredArticles.map((article, index) => (
            <Card 
              key={article.id} 
              className="group relative overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 bg-white border-0 animate-fadeInUp"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Badge catégorie */}
              <div className="absolute top-6 left-6 z-10">
                <Badge className="bg-white/90 text-gray-700 border border-gray-200 backdrop-blur-sm font-medium">
                  {article.category}
                </Badge>
              </div>

              {/* Image placeholder avec gradient */}
              <div className="h-48 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute bottom-4 right-4">
                  <div className="flex items-center gap-2 text-white text-sm font-medium">
                    <Clock className="h-4 w-4" />
                    <span>{article.readTime} min</span>
                  </div>
                </div>
                
                {/* Effet hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(article.publishedAt).toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}</span>
                </div>
                
                <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight">
                  {article.title}
                </CardTitle>
              </CardHeader>
              
              <CardContent className="pt-0">
                <CardDescription className="text-gray-600 mb-6 leading-relaxed">
                  {article.excerpt}
                </CardDescription>
                
                {/* Tags */}
                <div className="flex gap-2 mb-6">
                  {article.tags.slice(0, 2).map((tag) => (
                    <Badge 
                      key={tag} 
                      variant="outline" 
                      className="text-xs px-2 py-1 bg-gray-50 border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-colors"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                
                <Link to={`/article/${article.slug}`}>
                  <Button 
                    variant="ghost" 
                    className="btn-modern w-full justify-between p-0 h-auto text-blue-600 hover:text-blue-700 font-semibold group"
                  >
                    <span className="flex items-center">
                      <TrendingUp className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                      Lire l'article
                    </span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardContent>

              {/* Effet de brillance */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000"></div>
              </div>
            </Card>
          ))}
        </div>

        {/* Stats section */}
        <div className="mt-20">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-6 rounded-2xl glass border border-white/20">
              <div className="text-3xl font-bold text-gray-900 mb-2">65+</div>
              <div className="text-gray-600">Guides disponibles</div>
            </div>
            <div className="text-center p-6 rounded-2xl glass border border-white/20">
              <div className="text-3xl font-bold text-gray-900 mb-2">Weekly</div>
              <div className="text-gray-600">Nouveaux articles</div>
            </div>
            <div className="text-center p-6 rounded-2xl glass border border-white/20">
              <div className="text-3xl font-bold text-gray-900 mb-2">100%</div>
              <div className="text-gray-600">Validés par experts</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArticlesSection;